import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Alert,
  AlertColor,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import * as PromoContract from '@luxuryescapes/contract-svc-promo';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  InternalPromoWithDiscounts,
  PromoCondition,
  PromoConditionTypeConfig,
  PromoLoyaltyMetaConfig,
  PromoMeta,
  PromoProductTypes,
  PromoRegionGroupings,
  PromoTag,
  addPromo,
  createOrUpdatePromoDiscount,
  getPromoMeta,
  hasAdvanceRestrictions,
  mapLoyaltyPointsBonusConfigToV1,
  updatePromo,
} from '~/services/PromoService';
import { addYears, formatDateLongISO, formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';

import { getPromoTagArrays } from '~/utils/promoTags';

import { PROMO_DEVICE_TYPES, PROMO_TYPES } from '../../consts/promo';
import DateTimeWidget from '../Common/Elements/DateTimeWidget';
import Spinner from '../Common/Spinner';
import PromoConditions from '../Promo/PromoConditions';
import { PromoReplacementExplainer } from '../Promo/PromoReplacementExplainer';
import PromoUserBatchUpload from '../Promo/PromoUser/PromoUserBatchUpload';
import PromoUsers from '../Promo/PromoUsers';

import AllowedPaymentBrandSelect from './AllowedPaymentBrandSelect';
import AllowedPaymentMethodSelect from './AllowedPaymentMethodSelect';
import LoyaltyBonusApplyMethodSelector from './LoyaltyBonusApplyMethodSelector';
import ParentPromoUsageLimitWarning from './ParentPromoUsageLimitWarning';
import PromoDiscountFormV4 from './PromoDiscountFormV4';
import PromoSummaryActions from './PromoSummaryActions';

interface PromoFormType {
  code_name: string;
  description: string;
  department_tag: string;
  promo_type: 'percentage' | 'fixed_amount';
  expires_at: Date;
  code_limit: number;
  first_time_purchase_only: boolean;
  first_time_app_purchase_only: boolean;
  limit_per_user: number;
  limit_per_user_per_year: number;
  device_types: Array<string>;
  allowed_payment_methods: Array<string>;
  allowed_payment_brands: Array<string>;
  allowed_airline_carriers: Array<string>;
  is_corporate?: string;
  corporate_name?: string;
  email_domains?: string;
  email_addresses?: string;
  bin_numbers?: string;
  offer_whitelist?: string;
  offer_blacklist?: string;
  dept_tag: string;
  category_tag: string;
  sub_category_tag: string;
  promo_toggles: Array<PromoContract.definitions['PromoToggles']['key']>;
  popup_message: string;
  loyalty_bonus_apply_method: PromoLoyaltyMetaConfig['loyaltyBonusFilterMethods'][number]['key'];
}

// Having a presenter to ensure we only send relevant data when update
const promoPresenter = (data: PromoFormType, brand: string) => {
  return {
    code_name: data.code_name,
    description: data.description,
    promo_type: data.promo_type,
    expires_at: data.expires_at,
    code_limit: data.code_limit,
    limit_per_user: data.limit_per_user,
    limit_per_user_per_year: data?.limit_per_user_per_year || 0,
    first_time_purchase_only: data.first_time_purchase_only,
    first_time_app_purchase_only: data.first_time_app_purchase_only,
    device_types: data.device_types,
    allowed_payment_methods: data.allowed_payment_methods,
    allowed_payment_brands: data.allowed_payment_brands,
    is_corporate: data.is_corporate,
    corporate_name: data.is_corporate ? data.corporate_name : undefined,
    email_domains:
      data.email_domains && data.email_domains.length > 0
        ? data.email_domains
            .split(',')
            .map((email) => email.trim())
            .filter((email) => email.length > 0)
        : [],
    brand,
    email_addresses: data?.email_addresses
      ? data?.email_addresses
          ?.split(',')
          ?.map((email) => email?.trim())
          .filter((email) => email.length > 0)
      : [],
    offer_whitelist: data?.offer_whitelist
      ? data?.offer_whitelist
          ?.split(',')
          ?.map((offer) => offer?.trim())
          .filter((offer) => offer.length > 0)
      : [],
    offer_blacklist: data?.offer_blacklist
      ? data?.offer_blacklist
          ?.split(',')
          ?.map((offer) => offer?.trim())
          .filter((offer) => offer.length > 0)
      : [],
    bin_numbers: data?.bin_numbers
      ? data?.bin_numbers
          ?.split(',')
          ?.map((bin) => bin?.trim())
          .filter((bin) => bin.length > 0)
      : [],
    dept_tag: data.dept_tag,
    category_tag: data.category_tag,
    sub_category_tag: data.sub_category_tag,
    promo_toggles: data.promo_toggles ?? [],
    popup_message: data.popup_message,
    allowed_airline_carriers: data?.allowed_airline_carriers ?? [],
    loyalty_bonus_apply_method:
      data?.loyalty_bonus_apply_method ??
      ('NONE' as PromoLoyaltyMetaConfig['loyaltyBonusFilterMethods'][number]['key']),
  };
};

interface Props {
  promoCode: InternalPromoWithDiscounts | null;
  parentPromo?: InternalPromoWithDiscounts | null;
  rerender?: () => void;
  promoMeta: PromoMeta;
  isPromoMetaLoading: boolean;
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig>;
  isDev: boolean;
}

const formDefaultValues = {
  code_name: null,
  description: null,
  department_tag: null,
  promo_type: null,
  expires_at: formatDateLongISO(addYears(1)),
  code_limit: 1,
  limit_per_user: 1,
  limit_per_user_per_year: null,
  first_time_purchase_only: false,
  first_time_app_purchase_only: false,
  device_types: [],
  allowed_payment_methods: [],
  allowed_payment_brands: [],
  allowed_airline_carriers: [],
  is_corporate: false,
  usage_count: null,
  corporate_name: null,
  email_domains: null,
  email_addresses: null,
  offer_whitelist: null,
  offer_blacklist: null,
  bin_numbers: null,
  dept_tag: null,
  category_tag: null,
  sub_category_tag: null,
  promo_toggles: [],
  popup_message: '',
  loyalty_bonus_apply_method: null,
};
/**
 * @deprecated - see PromoFormV4
 */
function PromoFormV3({
  promoCode = null,
  parentPromo,
  rerender,
  promoMeta,
  isPromoMetaLoading,
  promoCodeConditionTypeConfig,
  isDev,
}: Props) {
  const { control, handleSubmit, watch, setValue } = useForm({
    mode: 'onChange',
    defaultValues: promoCode
      ? {
          ...promoCode,
          department_tag: promoCode?.department_tag,
          dept_tag: promoCode?.dept_tag,
          category_tag: promoCode?.category_tag,
          sub_category_tag: promoCode?.sub_category_tag,
          expires_at: promoCode?.expires_at,
          usage_count: promoCode?.usage_count,
          first_time_purchase_only: promoCode?.first_time_purchase_only,
          first_time_app_purchase_only: promoCode?.first_time_app_purchase_only,
          email_domains: promoCode?.email_domains?.length > 0 ? promoCode?.email_domains?.join(',') : '',
          email_addresses: promoCode?.email_addresses?.join(',') ?? '',
          offer_whitelist: promoCode?.offer_whitelist?.join(',') ?? '',
          offer_blacklist: promoCode?.offer_blacklist?.join(',') ?? '',
          bin_numbers: promoCode?.bin_numbers?.join(',') ?? '',
          promo_toggles: promoCode?.promo_toggles,
          allowed_airline_carriers: promoCode?.allowed_airline_carriers ?? [],
          loyalty_bonus_apply_method: promoCode?.loyalty_bonus_apply_method,
        }
      : formDefaultValues,
  });
  const [isDiscountReady, setIsDiscountReady] = useState(false);
  const [promoDiscountData, setPromoDiscountData] = useState<Array<App.PromoDiscount>>(
    promoCode?.promo_discounts ?? [],
  );
  const [selectedDiscountIds, setSelectedDiscountIds] = useState<Array<string> | 'all'>([]);
  const [selectedConditionIds, setSelectedConditionIds] = useState<Array<string>>([]);
  const [promoType, setPromoType] = useState(promoCode?.promo_type ?? undefined);

  const [promoTags, setPromoTags] = useState<Array<PromoTag>>(null);
  const [promoMetaLoading, setPromoMetaLoading] = useState<boolean>(true);

  const [promoDeptTagOptions, setPromoDeptTagOptions] = useState<Array<string>>([]);
  const [promoDeptTag, setPromoDeptTag] = useState<string>(promoCode?.dept_tag);
  const [promoCategoryTagOptions, setPromoCategoryTagOptions] = useState<Array<string>>([]);
  const [promoCategoryTag, setPromoCategoryTag] = useState<string>(promoCode?.category_tag);
  const [promoSubCategoryTagOptions, setPromoSubCategoryTagOptions] = useState<Array<string>>([]);
  const [promoSubCategoryTag, setPromoSubCategoryTag] = useState<string>(promoCode?.sub_category_tag ?? '');
  const [promoToggles, setPromoToggles] = useState<Array<PromoContract.definitions['PromoToggles']>>([]);

  const [promoProductTypes, setPromoProductTypes] = useState<PromoProductTypes>([]);
  const [promoRegionGroupings, setPromoRegionGroupings] = useState<PromoRegionGroupings>([]);
  const [promoLoyaltyMetaConfig, setPromoLoyaltyMetaConfig] = useState<PromoLoyaltyMetaConfig | null>(null);
  const [parentPromoDisplay, setParentPromoDisplay] = useState<string>(promoCode?.id_promo_code);

  const isCorporateBenefitOn = watch('is_corporate');
  const promoDescription = watch('description');

  const promoDepartmentTag = watch('department_tag');
  const offerWhiteList = watch('offer_whitelist');
  const offerBlackList = watch('offer_blacklist');
  const firstTimePurchaseOnly = watch('first_time_purchase_only');
  const firstTimeAppPurchaseOnly = watch('first_time_app_purchase_only');
  const deviceTypes = watch('device_types');
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const hasAdvancedRestrictions = hasAdvanceRestrictions(promoCode);
  const [showAdvanced, setShowAdvanced] = useState(hasAdvancedRestrictions);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { search } = useLocation();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const handleDiscountRowSelect = (discountId: string) => {
    const relatedConditions = promoCode?.promo_conditions?.filter(
      (condition) =>
        condition.promo_code_discount_ids?.includes(discountId) ||
        !condition.promo_code_discount_ids ||
        condition.promo_code_discount_ids.length === 0,
    );
    if (!relatedConditions) {
      return;
    }
    setSelectedConditionIds(relatedConditions.map((condition) => condition.id_promo_code_condition) ?? []);
    setSelectedDiscountIds([]);
  };

  const onSubmit = async (data) => {
    setHasSubmitted(true);
    try {
      const filteredData = promoPresenter(data, brand);
      if (promoCode) {
        await updatePromo(promoCode.id_promo_code, brand, filteredData);
        rerender?.();
      } else {
        const newPromoCode = await addPromo(filteredData);
        // Create promo discounts following the promo code
        Promise.all(
          promoDiscountData.map(async (discount) => {
            const payload = {
              id_promo_code: newPromoCode.result.id_promo_code,
              id_promo_code_discount: discount.id_promo_code_discount,
              discount_value: discount.discount_value,
              max_discount: discount.max_discount,
              subscription_item_discount_value: discount.subscription_item_discount_value,
              subscription_item_discount_type: discount.subscription_item_discount_type,
              min_spend: discount.min_spend,
              products: discount.products,
              region: discount.region,
              loyalty_points_bonus_config: mapLoyaltyPointsBonusConfigToV1(discount.loyalty_points_bonus_config),
            };
            return await createOrUpdatePromoDiscount(payload);
          }),
        );
        setTimeout(() => history.push(`/promos/${newPromoCode.result.id_promo_code}${search}`), 5000);
      }
      enqueueSnackbar(
        `Success: ${
          promoCode
            ? 'Your changes have been saved.'
            : 'Your promo code has been created. You will now be redirected to the edit page.'
        }`,
        {
          autoHideDuration: 5000,
        },
      );
    } catch (err) {
      if ('errors' in err && err.errors?.length > 0) {
        enqueueSnackbar(`Error: ${JSON.stringify(err, null, 4)}`, {
          variant: 'error',
          autoHideDuration: 5000,
        });
      } else {
        enqueueSnackbar(`${err.message}`, {
          autoHideDuration: 5000,
        });
      }
    }
  };

  useEffect(() => {
    if (!isCorporateBenefitOn) {
      setValue('corporate_name', '');
      setValue('email_domains', '');
    }
  }, [isCorporateBenefitOn, setValue]);

  useEffect(() => {
    const fetchMeta = async () => {
      setPromoMetaLoading(true);
      await getPromoMeta()
        .then((res) => {
          setPromoTags(res.result.promo_tags);
          setPromoToggles(res.result.promo_toggles);
          refreshMetaOptions(res.result.promo_tags, promoCode?.dept_tag, promoCode?.category_tag);
          setPromoProductTypes(res.result.promo_product_types);
          setPromoRegionGroupings(res.result.promo_region_groupings);
          setPromoLoyaltyMetaConfig(res.result.promo_loyalty_config);
          return res;
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setPromoMetaLoading(false);
        });
    };
    fetchMeta();
  }, []);

  const refreshMetaOptions = (promoTags: Array<PromoTag>, selectedDepartment: string, selectedCategory: string) => {
    const tagArrays = getPromoTagArrays({ promoTags, selectedDepartment, selectedCategory, usersPromoRoles: null });
    setPromoDeptTagOptions(tagArrays.departmentTags);
    setPromoCategoryTagOptions(tagArrays.categoryTags);
    setPromoSubCategoryTagOptions(tagArrays.subCategoryTags);
    if (tagArrays.categoryTags.length == 1) {
      setPromoCategoryTag(tagArrays.categoryTags[0]);
      setValue('category_tag', tagArrays.categoryTags[0]);
    } else if (tagArrays.categoryTags.length == 0) {
      setPromoCategoryTag('');
      setValue('category_tag', '');
    }
    if (tagArrays.subCategoryTags.length == 1) {
      setPromoSubCategoryTag(tagArrays.subCategoryTags[0]);
      setValue('sub_category_tag', tagArrays.subCategoryTags[0]);
    } else if (tagArrays.subCategoryTags.length == 0) {
      setPromoSubCategoryTag('');
      setValue('sub_category_tag', '');
    }
  };

  const createdAt = formatDateLongMonthWithMeridiemNoSeconds(promoCode?.created_at);
  const updatedAt = formatDateLongMonthWithMeridiemNoSeconds(promoCode?.updated_at);

  const handleConditionRowSelect = (condition: PromoCondition | null) => {
    setSelectedDiscountIds(condition?.promo_code_discount_ids?.length > 0 ? condition?.promo_code_discount_ids : 'all');
    setSelectedConditionIds([]);
  };

  const handleReset = () => {
    setSelectedDiscountIds([]);
    setSelectedConditionIds([]);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {promoCode?.promo_warnings?.length > 0 && (
          <Grid container spacing={2}>
            {promoCode.promo_warnings.map((warning) => (
              <Grid
                size={{ xs: 12 }}
                key={`${warning.code}-${warning.id_promo_code}-${warning.id_promo_code_discount}`}
              >
                <Alert severity={warning.level as AlertColor}>
                  {warning.code}
                  {warning.message && <Typography variant="subtitle2">{warning.message}</Typography>}
                </Alert>
              </Grid>
            ))}
          </Grid>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <PromoSummaryActions promoCode={promoCode} parentPromoCode={parentPromo} promoMeta={promoMeta} />
          </Grid>
          <Controller
            key="code_name"
            name="code_name"
            control={control}
            render={({ field, fieldState }) => (
              <Grid size={{ xs: 5 }}>
                <TextField
                  {...field}
                  fullWidth
                  label="Code Name"
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : ''}
                />
              </Grid>
            )}
            rules={{
              required: 'Promo code name is required',
              pattern: {
                value: /^[A-Za-z0-9_-]+$/,
                message: 'Promo code name can only include alphanumeric characters, underscores, and dashes',
              },
            }}
          />
          <Controller
            key="promo_type"
            name="promo_type"
            control={control}
            render={({ field }) => (
              <Grid size={{ xs: 5 }}>
                <Autocomplete
                  {...field}
                  disabled={!!promoCode}
                  options={PROMO_TYPES.map((type) => type.key)}
                  getOptionLabel={(option) => PROMO_TYPES.find((type) => type.key === option)?.value}
                  onChange={(e, newValue) => {
                    setPromoType(newValue);
                    field.onChange(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} required variant="outlined" label="Promo Type" />}
                />
              </Grid>
            )}
            rules={{ required: true }}
          />
          {promoCode && (
            <LoyaltyBonusApplyMethodSelector
              promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
              isPromoMetaLoading={isPromoMetaLoading}
              control={control}
            />
          )}
          <Controller
            key="description"
            name="description"
            control={control}
            render={({ field }) => (
              <Grid size={{ xs: 12 }}>
                <TextField {...field} fullWidth label="Description" required />
              </Grid>
            )}
            rules={{ required: true }}
          />
          {promoMetaLoading && <Spinner />}
          {!promoMetaLoading && promoDeptTagOptions.length > 0 && (
            <Controller
              key="dept_tag"
              name="dept_tag"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Grid size={{ xs: 4 }}>
                  <Autocomplete
                    {...field}
                    value={promoDeptTag}
                    options={promoDeptTagOptions}
                    renderInput={(params) => <TextField required {...params} variant="outlined" label="Department" />}
                    onChange={(e: unknown, newValue: string | null) => {
                      field.onChange(newValue);
                      setPromoDeptTag(newValue);
                      setPromoCategoryTag(null);
                      setPromoSubCategoryTag(null);
                      refreshMetaOptions(promoTags, newValue, null);
                    }}
                  />
                </Grid>
              )}
            />
          )}
          {!promoMetaLoading && promoCategoryTagOptions.length > 0 && (
            <Controller
              key="category_tag"
              name="category_tag"
              control={control}
              rules={{ required: promoCategoryTagOptions.length > 0 }}
              render={({ field }) => (
                <Grid size={{ xs: 4 }}>
                  <Autocomplete
                    {...field}
                    value={promoCategoryTag}
                    options={promoCategoryTagOptions}
                    renderInput={(params) => <TextField required {...params} variant="outlined" label="Category" />}
                    onChange={(e: unknown, newValue: string | null) => {
                      field.onChange(newValue);
                      setPromoCategoryTag(newValue);
                      setPromoSubCategoryTag(null);
                      refreshMetaOptions(promoTags, promoDeptTag, newValue);
                    }}
                  />
                </Grid>
              )}
            />
          )}
          {!promoMetaLoading && promoSubCategoryTagOptions.length > 0 && (
            <Controller
              key="sub_category_tag"
              name="sub_category_tag"
              rules={{ required: promoSubCategoryTagOptions.length > 0 }}
              control={control}
              render={({ field }) => (
                <Grid size={{ xs: 4 }}>
                  <Autocomplete
                    {...field}
                    value={promoSubCategoryTag}
                    options={promoSubCategoryTagOptions}
                    renderInput={(params) => <TextField required {...params} variant="outlined" label="Sub Category" />}
                    onChange={(e: unknown, newValue: string | null) => {
                      field.onChange(newValue);
                      setPromoSubCategoryTag(newValue);
                    }}
                  />
                </Grid>
              )}
            />
          )}
          <Grid size={{ xs: 12 }}>
            <Box>
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {(selectedDiscountIds.length > 0 || selectedConditionIds.length > 0) && (
              <Button onClick={handleReset}>Reset Highlighting</Button>
            )}
            {!promoMetaLoading ? (
              <PromoDiscountFormV4
                promoCode={promoCode}
                promoWarnings={promoCode?.promo_warnings?.filter((warning) => !warning.is_dev)}
                parentPromo={parentPromo}
                promoType={promoType}
                promoDiscountData={promoDiscountData}
                setPromoDiscountData={setPromoDiscountData}
                promoProductTypes={promoProductTypes}
                promoRegionGroupings={promoRegionGroupings}
                promoMetaLoading={promoMetaLoading}
                rerender={rerender}
                highlightedDiscountIds={selectedDiscountIds}
                onDiscountRowSelect={handleDiscountRowSelect}
                promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
                promoLoyaltyBonusApplyMethod={watch('loyalty_bonus_apply_method')}
              />
            ) : (
              <Spinner />
            )}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <PageSubheader title="Promo code restrictions" />
            {(selectedDiscountIds.length > 0 || selectedConditionIds.length > 0) && (
              <Button onClick={handleReset}>Reset Highlighting</Button>
            )}
          </Grid>
          <Grid size={{ xs: 12 }}>
            {promoMeta && promoCode?.promo_conditions !== undefined && (
              <PromoConditions
                promoCode={promoCode}
                promoCodeConditionTypeConfig={promoCodeConditionTypeConfig}
                onConditionRowSelect={handleConditionRowSelect}
                highlightedConditionIds={selectedConditionIds}
                onConditionUpdate={rerender}
              />
            )}
          </Grid>
          <Controller
            key="expires_at"
            name="expires_at"
            control={control}
            render={({ field }) => (
              <Grid size={{ xs: 12 }}>
                {Boolean(field.value) && <DateTimeWidget format="DD/MM/YYYY hh:mm a" label="Expiry Date" {...field} />}
                <Typography variant="subtitle2">
                  (There is a grace period of 24 hours - to stop use immediately - set the promo_toggle of "Disallow all
                  promo requests")
                </Typography>
              </Grid>
            )}
          />
          <Controller
            key="code_limit"
            name="code_limit"
            control={control}
            rules={{ required: true, min: 1 }}
            render={({ field }) => (
              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Total Usage Limit"
                  type="number"
                  onChange={(e) => {
                    const targetValue = parseInt(e.target.value);
                    field.onChange(Math.max(targetValue, 1));
                  }}
                />
                {parentPromo && promoCode && (
                  <ParentPromoUsageLimitWarning
                    parentPromo={parentPromo}
                    childPromo={promoCode}
                    onEmancipated={() => rerender?.()}
                  />
                )}
              </Grid>
            )}
          />
          <Controller
            key="limit_per_user"
            name="limit_per_user"
            control={control}
            rules={{ required: true, min: 1 }}
            render={({ field }) => (
              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  {...field}
                  fullWidth
                  label="Usage Limit Per Customer"
                  type="number"
                  onChange={(e) => {
                    const targetValue = parseInt(e.target.value);
                    field.onChange(Math.max(targetValue, 1));
                  }}
                />
              </Grid>
            )}
          />
          <Controller
            key="limit_per_user_per_year"
            name="limit_per_user_per_year"
            control={control}
            rules={{ required: false, min: 0 }}
            render={({ field }) => (
              <Grid size={{ xs: 12 }}>
                <TextField
                  {...field}
                  fullWidth
                  label="Usage Limit Per Customer Per Year"
                  type="number"
                  onChange={(e) => {
                    const targetValue = parseInt(e.target.value);
                    field.onChange(Math.max(targetValue, 0));
                  }}
                />
                <Typography variant="subtitle2">
                  (Usage calculated based on purchases in the last 365 days - 0 applies no yearly limit)
                </Typography>
              </Grid>
            )}
          />
          <Grid size={{ xs: 12 }}>
            <PageSubheader title="Presentation" />
            <Box my={2}>
              <Controller
                key="popup_message"
                name="popup_message"
                control={control}
                render={({ field }) => (
                  <Grid size={{ xs: 12 }}>
                    <TextField {...field} fullWidth multiline label="Popup Message" />
                  </Grid>
                )}
              />
            </Box>
            {promoCode?.popup_message_presented && (
              <Box my={2}>
                <TextField
                  value={promoCode?.popup_message_presented}
                  disabled
                  fullWidth
                  multiline
                  label="Popup Message"
                />
              </Box>
            )}
            {promoMeta && <PromoReplacementExplainer promoTextReplacements={promoMeta.promo_text_replacements} />}
          </Grid>
          <Box margin={2}>
            <Button variant="text" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Hide ' : 'Show '} Advanced Restrictions
            </Button>
          </Box>
          {showAdvanced && (
            <>
              <Grid size={{ xs: 12 }}>
                <Grid container marginLeft={2}>
                  <Controller
                    key="first_time_purchase_only"
                    name="first_time_purchase_only"
                    control={control}
                    render={({ field }) => (
                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={<Checkbox {...field} defaultChecked={promoCode?.first_time_purchase_only} />}
                          label="First time purchase only"
                          title="If checked, only customers who have not placed an order can use the promo (brand specific)"
                        />
                      </Grid>
                    )}
                  />
                  <Controller
                    key="first_time_app_purchase_only"
                    name="first_time_app_purchase_only"
                    control={control}
                    render={({ field }) => (
                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={<Checkbox {...field} defaultChecked={promoCode?.first_time_app_purchase_only} />}
                          label="First time app purchase only"
                          title="If checked, only customers who have not placed an order in a mobile app can use the promo (brand specific)"
                        />
                      </Grid>
                    )}
                  />
                </Grid>
                {!promoMetaLoading && promoToggles.length > 0 && (
                  <Controller
                    key="promo_toggles"
                    name="promo_toggles"
                    control={control}
                    render={({ field }) => (
                      <Grid size={{ xs: 12 }}>
                        <Autocomplete
                          multiple
                          options={promoToggles}
                          getOptionLabel={(option) =>
                            `${option.isActive ? '[INACTIVE] - ' : ''}${option.internalDescription}`
                          }
                          value={promoToggles.filter((option) => field?.value?.includes(option.key)) ?? []}
                          onChange={(event, newValue) => {
                            const selectedKeys = newValue.map((option) => option.key);
                            field.onChange(selectedKeys);
                          }}
                          renderInput={(params) => {
                            return <TextField {...params} variant="outlined" label="Toggles" />;
                          }}
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              title={
                                option.isActive
                                  ? option.internalDescriptionLong
                                  : `Inactive - ${option.internalDescriptionLong}`
                              }
                            >
                              {`${!option.isActive ? '[INACTIVE] - ' : ''}${option.internalDescription}`}
                            </li>
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={option.key}
                                {...getTagProps({ index })}
                                label={`${!option.isActive ? '[INACTIVE] - ' : ''}${option.internalDescription}`}
                                title={
                                  option.isActive
                                    ? option.internalDescriptionLong
                                    : `Inactive - this toggle will not be applied - ${option.internalDescriptionLong}`
                                }
                              />
                            ))
                          }
                        />
                      </Grid>
                    )}
                  />
                )}
              </Grid>
              <Controller
                key="device_types"
                name="device_types"
                control={control}
                render={({ field }) => (
                  <Grid size={{ xs: 12 }}>
                    <Autocomplete
                      {...field}
                      multiple
                      options={PROMO_DEVICE_TYPES.slice(1).map((type) => type.key)}
                      getOptionLabel={(option) => PROMO_DEVICE_TYPES.find((type) => type.key === option)?.value}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          {...params}
                          variant="outlined"
                          label="Restrict to Device Types"
                        ></TextField>
                      )}
                      onChange={(e: unknown, newValue: Array<string> | null) => {
                        field.onChange(newValue);
                      }}
                      // Styling the dropdown component
                      componentsProps={{
                        paper: {
                          sx: {
                            boxShadow: `0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)`,
                          },
                        },
                      }}
                    />
                  </Grid>
                )}
              />
              {promoMeta &&
                promoMeta.promo_allowed_payment_brands &&
                promoMeta.promo_allowed_payment_brands.length > 0 && (
                  <AllowedPaymentBrandSelect
                    control={control}
                    selectedValues={watch('allowed_payment_brands') ?? []}
                    allowedPaymentBrands={promoMeta.promo_allowed_payment_brands ?? []}
                    isPromoMetaLoading={isPromoMetaLoading}
                  />
                )}
              {promoMeta &&
                promoMeta.promo_allowed_payment_methods &&
                promoMeta.promo_allowed_payment_methods.length > 0 && (
                  <AllowedPaymentMethodSelect
                    selectedValues={watch('allowed_payment_methods')}
                    control={control}
                    promoMeta={promoMeta}
                    isPromoMetaLoading={isPromoMetaLoading}
                  />
                )}
              <Grid size={{ xs: 12 }}>
                <Controller
                  key="bin_numbers"
                  name="bin_numbers"
                  control={control}
                  render={({ field }) => (
                    <Tooltip title="You must restrict to at least Stripe and/or Stripe 3DS payment methods. Multiple BIN numbers must be comma separated.">
                      <TextField {...field} fullWidth label="Restrict to Bank Identification Number(s)" />
                    </Tooltip>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  key="email_addresses"
                  name="email_addresses"
                  control={control}
                  render={({ field }) => (
                    <Tooltip title="Multiple email address(es) must be comma separated.">
                      <TextField {...field} fullWidth label="Restrict to Email Address(es)" />
                    </Tooltip>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  key="offer_whitelist"
                  name="offer_whitelist"
                  control={control}
                  render={({ field }) => (
                    <Tooltip title="Multiple deal ID(s) must be comma separated.">
                      <TextField
                        {...field}
                        fullWidth
                        disabled={offerBlackList && offerBlackList.length > 0}
                        label="Restrict to Deal ID(s) [deprecated - see offer-id condition]"
                      />
                    </Tooltip>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  key="offer_blacklist"
                  name="offer_blacklist"
                  control={control}
                  render={({ field }) => (
                    <Tooltip title="Multiple deal ID(s) must be comma separated.">
                      <TextField
                        {...field}
                        fullWidth
                        disabled={offerWhiteList && offerWhiteList.length > 0}
                        label="Exclude Deal ID(s) [deprecated - see offer-id condition]"
                      />
                    </Tooltip>
                  )}
                />
              </Grid>
            </>
          )}
          {showAdvanced && (
            <>
              <Grid size={{ xs: 12 }}>
                <PageSubheader title="Corporate benefits" />
              </Grid>
              <Controller
                key="is_corporate"
                name="is_corporate"
                control={control}
                render={({ field }) => (
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={<Checkbox disabled={!!promoCode} defaultChecked={isCorporateBenefitOn} {...field} />}
                      label="Corporate promo code"
                    />
                  </Grid>
                )}
              />
              {isCorporateBenefitOn && (
                <>
                  <Controller
                    key="corporate_name"
                    name="corporate_name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          disabled={!!promoCode}
                          fullWidth
                          required
                          {...field}
                          label="Company Name"
                        ></TextField>
                      </Grid>
                    )}
                  />

                  <Controller
                    key="email_domains"
                    name="email_domains"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!isCorporateBenefitOn) {
                          return true;
                        }
                        return value && value.trim().length > 0
                          ? true
                          : 'Email domains are required for corporate promo codes';
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <Grid size={{ xs: 12 }}>
                        <Tooltip title="Multiple email domains must be comma separated">
                          <TextField
                            fullWidth
                            {...field}
                            label="Eligible Email Domains"
                            required
                            error={!!fieldState.error}
                            helperText={fieldState.error ? fieldState.error.message : ''}
                          />
                        </Tooltip>
                      </Grid>
                    )}
                  />
                </>
              )}
            </>
          )}
          <Grid size={{ xs: 12 }}>
            <Box>
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {showAdvanced && !!promoCode && (
        <Grid size={{ xs: 12 }}>
          <PageSubheader title="Users">
            <Box component="span" color="text.secondary" alignItems="left">
              Users that qualify for this promo
            </Box>
          </PageSubheader>
          <PromoUsers promoId={promoCode?.id_promo_code} promoCode={promoCode} setShowAdvanced={setShowAdvanced} />
          <PromoUserBatchUpload codeName={promoCode?.code_name} promoExpiresAt={promoCode?.expires_at} />
        </Grid>
      )}
    </>
  );
}

export default PromoFormV3;
